import { CloseOutlined } from '@ant-design/icons'
import { Space } from 'antd'
import CampaignIcon from 'app/components/Core/Campaign/Icon'
import { parseTeaserContent } from 'app/components/Core/Campaign/teaserContent'
import Button from 'app/components/UI-Elements/General/Button'
import { LocaleEnum } from 'app/constants/Enums'
import { useSession } from 'app/lib/hooks/useCurrent'
import { useQuery } from 'app/lib/hooks/useQuery'
import useShortViewport from 'app/lib/hooks/useShortViewport'
import useTranslation from 'app/lib/hooks/useTranslation'
import StorageService from 'app/lib/services/StorageService'
import { CURRENT_MANAGER_CAMPAIGNS_QUERY } from 'app/queries/Campaigns'
import * as React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

type CurrentManagerCampaignsQuery = {
  currentCampaigns: ICampaign[]
}

const SidebarTeaser: React.FC = () => {
  const { tComponent } = useTranslation({ componentType: 'navigation', scope: 'sidebar' })
  const navigate = useNavigate()
  const { langCode } = useSession()
  const short = useShortViewport()
  const { data } = useQuery<CurrentManagerCampaignsQuery>(CURRENT_MANAGER_CAMPAIGNS_QUERY, {
    fetchPolicy: 'cache-first'
  })
  const [dismissed, setDismissed] = useState(true)

  const campaign = data?.currentCampaigns?.find(({ placement }) => placement === 'sidebar')
  // Dismissed per device and per campaign, so the next campaign shows again.
  const storageKey = campaign && `campaign_${campaign.id}_dismissed`
  const html =
    (langCode === LocaleEnum.en ? campaign?.contentEnHtml : campaign?.contentDeHtml) ||
    campaign?.contentDeHtml
  const content = useMemo(() => (html ? parseTeaserContent(html) : undefined), [html])

  useEffect(() => {
    if (!storageKey) return
    StorageService.get({ key: storageKey }).then(({ value }) => setDismissed(value === 'true'))
  }, [storageKey])

  if (!campaign || !storageKey || !content || dismissed) return null

  const { title, descriptionHtml } = content
  const english = langCode === LocaleEnum.en
  const buttonLabel = (english && campaign.buttonLabelEn) || campaign.buttonLabelDe
  const { buttonPath } = campaign

  const dismiss = () => {
    setDismissed(true)
    StorageService.set({ key: storageKey, value: 'true' })
  }

  const follow = () => {
    if (!buttonPath) return
    if (buttonPath.startsWith('/')) navigate(buttonPath)
    else window.open(buttonPath, '_blank', 'noopener')
  }

  return (
    <div className="sider-teaser" data-testid="sidebar-teaser">
      <Button
        type="link"
        className="sider-teaser__close"
        icon={<CloseOutlined />}
        onClick={dismiss}
        aria-label={tComponent({ id: 'teaser.dismiss' })}
      />
      <Space direction="vertical" size={short ? 2 : 4}>
        {title && (
          <span className="text-bold text-primary">
            <CampaignIcon id={campaign.icon} className="mr-2" />
            {title}
          </span>
        )}
        {descriptionHtml && (
          <div
            className="sider-teaser__description text-muted"
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
        )}
        {buttonLabel && buttonPath && (
          <Button type="secondary" size={short ? 'small' : 'middle'} onClick={follow}>
            {buttonLabel}
          </Button>
        )}
      </Space>
    </div>
  )
}

export default SidebarTeaser
